import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Instagram, Facebook, Linkedin } from "lucide-react";
import { LOGO_URL } from "../lib/api";
import { EDGE, EDGE_LOGO } from "../lib/edge";
import EhoBadge from "./EhoBadge";

export default function Footer() {
  return (
    <footer
      className="bg-[#1A1A1A] text-white mt-24"
      data-testid="site-footer"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="mb-5">
              <div className="bg-white rounded-lg p-3 inline-block">
                <img
                  src={LOGO_URL}
                  alt="Gens Realty — Houston, TX"
                  className="h-20 w-auto object-contain"
                />
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-400 mt-3">
                Houston, TX
              </div>
            </div>
            <p className="text-sm text-gray-400 leading-relaxed max-w-xs">
              Boutique residential real estate brokerage serving Houston, Texas with white-glove service since 2010.
            </p>
            <div className="mt-6">
              <EhoBadge
                variant="opportunity"
                size={48}
                color="#FFFFFF"
                textColor="#FFFFFF"
              />
            </div>
            <div className="flex gap-3 mt-6">
              <a href="#" aria-label="Instagram" className="w-9 h-9 rounded-full border border-gray-700 flex items-center justify-center hover:border-[#E63946] hover:text-[#E63946] transition-colors" data-testid="footer-instagram">
                <Instagram className="w-4 h-4" strokeWidth={1.5} />
              </a>
              <a href="#" aria-label="Facebook" className="w-9 h-9 rounded-full border border-gray-700 flex items-center justify-center hover:border-[#E63946] hover:text-[#E63946] transition-colors" data-testid="footer-facebook">
                <Facebook className="w-4 h-4" strokeWidth={1.5} />
              </a>
              <a href="#" aria-label="LinkedIn" className="w-9 h-9 rounded-full border border-gray-700 flex items-center justify-center hover:border-[#E63946] hover:text-[#E63946] transition-colors" data-testid="footer-linkedin">
                <Linkedin className="w-4 h-4" strokeWidth={1.5} />
              </a>
            </div>
          </div>

          {/* Real Estate */}
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-5">Real Estate</div>
            <ul className="space-y-3 text-sm text-gray-300">
              <li><Link to="/properties" className="hover:text-[#E63946] transition" data-testid="footer-properties">All Properties</Link></li>
              <li><Link to="/neighborhoods" className="hover:text-[#E63946] transition" data-testid="footer-neighborhoods">Neighborhoods</Link></li>
              <li><Link to="/mls-search" className="hover:text-[#E63946] transition">MLS Search</Link></li>
              <li><Link to="/home-value" className="hover:text-[#E63946] transition" data-testid="footer-homevalue">Home Value</Link></li>
              <li><Link to="/about" className="hover:text-[#E63946] transition" data-testid="footer-about">About</Link></li>
            </ul>
          </div>

          {/* Mortgage */}
          <div>
            <div className="flex items-center gap-2 mb-5">
              <div className="bg-white rounded p-1">
                <img src={EDGE_LOGO} alt="Edge Home Finance" className="h-5 w-auto" />
              </div>
              <div className="text-xs uppercase tracking-[0.2em] text-gray-500">Mortgage</div>
            </div>
            <ul className="space-y-3 text-sm text-gray-300">
              <li><Link to="/mortgage" className="hover:text-[#E63946] transition" data-testid="footer-mortgage">Mortgage Overview</Link></li>
              <li><Link to="/loan-programs" className="hover:text-[#E63946] transition">Loan Programs</Link></li>
              <li><Link to="/pre-approval" className="hover:text-[#E63946] transition">Get Pre-Approved</Link></li>
              <li><Link to="/refinance" className="hover:text-[#E63946] transition">Refinance</Link></li>
              <li><Link to="/loan-officers" className="hover:text-[#E63946] transition">Loan Officer</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-5">Contact</div>
            <ul className="space-y-4 text-sm text-gray-300">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 text-[#E63946]" strokeWidth={1.75} />
                <span>14911 Heritage Wood Drive<br />Houston, TX 77082</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-[#E63946]" strokeWidth={1.75} />
                <a href="tel:8327414662" className="hover:text-white">(832) 741-4662</a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-[#E63946]" strokeWidth={1.75} />
                <a href="mailto:PJoseph@gensrealty.com" className="hover:text-white">PJoseph@gensrealty.com</a>
              </li>
            </ul>
          </div>
        </div>

        {/* Edge Home Finance partner block */}
        <div className="mt-16 pt-10 border-t border-gray-800 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-3">
            <div className="flex items-start gap-3">
              <div className="bg-white rounded-md p-2.5">
                <img src={EDGE_LOGO} alt="Edge Home Finance" className="h-14 w-auto" />
              </div>
              <div>
                <div className="font-display text-base font-semibold">{EDGE.legal}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mt-1">
                  NMLS #{EDGE.nmls}
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-9 text-xs text-gray-400 leading-relaxed">
            <p>
              Mortgage services provided by <strong className="text-gray-200">{EDGE.legal}</strong>{" "}
              — {EDGE.address.street}, {EDGE.address.city}. {EDGE.phone} · Toll-Free {EDGE.tollFree} ·{" "}
              <a href={`mailto:${EDGE.email}`} className="text-gray-200 hover:text-white">{EDGE.email}</a>.
              Loan Officer: {EDGE.officer.name}, NMLS #{EDGE.officer.nmls}.
            </p>
            <p className="mt-2">
              <strong className="text-gray-200">Affiliated Business Arrangement:</strong> {EDGE.officer.name} is a Realtor
              (#{EDGE.officer.realtorNumber}) with Gens Realty, LLC and a Loan Originator with {EDGE.legal}. The two are{" "}
              <strong>separate companies</strong>; you are not required to use {EDGE.legal} for your purchase or sale.
              Equal Housing Opportunity Lender. Verify licensing at{" "}
              <a href="https://www.nmlsconsumeraccess.org/" target="_blank" rel="noopener noreferrer" className="text-gray-200 hover:text-white underline">
                NMLS Consumer Access
              </a>.
            </p>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-gray-800 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-gray-500">
          <div>© {new Date().getFullYear()} Gens Realty, LLC. All rights reserved.</div>
          <div className="flex gap-6">
            <Link to="/privacy" className="hover:text-white" data-testid="footer-privacy">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white" data-testid="footer-terms">Terms of Service</Link>
            <Link to="/fair-housing" className="hover:text-white" data-testid="footer-fair-housing">Fair Housing</Link>
            <Link to="/iabs" className="hover:text-white" data-testid="footer-iabs">IABS</Link>
            <Link to="/consumer-protection" className="hover:text-white" data-testid="footer-consumer-protection">Consumer Protection</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
